import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import {
  ShoppingCart, Trash2, Plus, Minus, CreditCard, ShoppingBag, ArrowLeft,
  Package, RefreshCw, AlertCircle, CheckCircle, XCircle
} from "lucide-react";
import { getCart, updateCartItem, removeFromCart, clearCart, checkoutCart, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";

function CartItem({ item, onUpdateQuantity, onRemove, isUpdating }) {
  const listing = item.listing;
  
  return (
    <Card className="bg-white border-[#E5E0D5]" data-testid={`cart-item-${item.listing_id}`}>
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Image */}
          <div className="w-24 h-24 rounded-lg overflow-hidden bg-[#F0EBE0] flex-shrink-0">
            {listing?.image_url ? (
              <img src={resolveImageUrl(listing.image_url)} alt={listing.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="w-8 h-8 text-[#6B665E]" />
              </div>
            )}
          </div>

          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-['Nunito'] font-semibold text-[#2D2A26] line-clamp-1" data-testid="cart-item-title">
                  {listing?.title}
                </h3>
                <p className="text-xs text-[#6B665E] font-['Nunito'] mt-0.5 line-clamp-1">
                  {listing?.description}
                </p>
                {listing?.seller && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <Avatar className="h-5 w-5">
                      <AvatarImage src={listing.seller?.picture} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">
                        {listing.seller?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <span className="text-xs text-[#6B665E] font-['Nunito']">{listing.seller?.name}</span>
                  </div>
                )}
              </div>
              <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)] text-lg flex-shrink-0">
                ${listing?.price}
              </span>
            </div>

            {/* Quantity Controls */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdateQuantity(item.listing_id, item.quantity - 1)}
                  disabled={isUpdating || item.quantity <= 1}
                  className="h-8 w-8 p-0 border-[#E5E0D5]"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </Button>
                <span className="font-['Nunito'] font-semibold text-[#2D2A26] w-8 text-center" data-testid="item-quantity">
                  {item.quantity}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onUpdateQuantity(item.listing_id, item.quantity + 1)}
                  disabled={isUpdating}
                  className="h-8 w-8 p-0 border-[#E5E0D5]"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>

              <div className="flex items-center gap-4">
                <span className="font-['Nunito'] font-bold text-[var(--liturgical-primary)]" data-testid="item-subtotal">
                  ${item.subtotal?.toFixed(2)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(item.listing_id)}
                  disabled={isUpdating}
                  className="text-[#991B1B] hover:text-[#7F1D1D] hover:bg-red-50"
                  data-testid="remove-item"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function CartPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    loadCart();
    
    // Handle payment status from URL
    const payment = searchParams.get("payment");
    if (payment === "cancelled") {
      toast.error("Payment was cancelled");
      // Clear the URL params
      window.history.replaceState({}, "", "/cart");
    }
  }, []);

  const loadCart = async () => {
    setLoading(true);
    try {
      const res = await getCart();
      setCart(res.data);
    } catch (err) {
      toast.error("Failed to load cart");
    }
    setLoading(false);
  };

  const handleUpdateQuantity = async (listingId, quantity) => {
    if (quantity < 1) {
      handleRemoveItem(listingId);
      return;
    }
    
    setUpdating(true);
    try {
      await updateCartItem(listingId, quantity);
      await loadCart();
    } catch (err) {
      toast.error("Failed to update quantity");
    }
    setUpdating(false);
  };

  const handleRemoveItem = async (listingId) => {
    setUpdating(true);
    try {
      await removeFromCart(listingId);
      toast.success("Item removed from cart");
      await loadCart();
    } catch (err) {
      toast.error("Failed to remove item");
    }
    setUpdating(false);
  };

  const handleClearCart = async () => {
    if (!confirm("Are you sure you want to clear your cart?")) return;
    
    setUpdating(true);
    try {
      await clearCart();
      toast.success("Cart cleared");
      await loadCart();
    } catch (err) {
      toast.error("Failed to clear cart");
    }
    setUpdating(false);
  };

  const handleCheckout = async () => {
    if (!cart?.items?.length) return;
    
    setCheckingOut(true);
    try {
      const res = await checkoutCart(window.location.origin);
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate checkout");
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  const isEmpty = !cart?.items?.length;

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="cart-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/marketplace")}
              className="text-[#6B665E] hover:text-[#2D2A26]"
              data-testid="back-to-marketplace"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Marketplace
            </Button>
          </div>
          {!isEmpty && (
            <Button
              variant="outline"
              onClick={handleClearCart}
              disabled={updating}
              className="border-[#E5E0D5] text-[#991B1B] hover:bg-red-50"
              data-testid="clear-cart-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Clear Cart
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
            <ShoppingCart className="w-6 h-6 text-[var(--liturgical-primary)]" />
          </div>
          <div>
            <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26]" data-testid="cart-title">
              Shopping Cart
            </h1>
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              {isEmpty ? "Your cart is empty" : `${cart.item_count} item${cart.item_count !== 1 ? 's' : ''} in your cart`}
            </p>
          </div>
        </div>

        {isEmpty ? (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-16 text-center">
              <ShoppingBag className="w-16 h-16 text-[#E5E0D5] mx-auto mb-4" />
              <h2 className="font-['Nunito'] font-semibold text-xl text-[#2D2A26] mb-2">
                Your cart is empty
              </h2>
              <p className="text-sm text-[#6B665E] font-['Nunito'] mb-6">
                Browse the marketplace to find items from your parish community
              </p>
              <Button
                onClick={() => navigate("/marketplace")}
                className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito']"
                data-testid="browse-marketplace-btn"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Browse Marketplace
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4">
              {cart.items.map((item) => (
                <CartItem
                  key={item.listing_id}
                  item={item}
                  onUpdateQuantity={handleUpdateQuantity}
                  onRemove={handleRemoveItem}
                  isUpdating={updating}
                />
              ))}
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="bg-white border-[#E5E0D5] sticky top-24">
                <CardHeader className="pb-4">
                  <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
                    Order Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    {cart.items.map((item) => (
                      <div key={item.listing_id} className="flex justify-between text-sm font-['Nunito']">
                        <span className="text-[#6B665E] truncate flex-1 mr-2">
                          {item.listing?.title} x{item.quantity}
                        </span>
                        <span className="text-[#2D2A26]">${item.subtotal?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-[#E5E0D5] pt-4">
                    <div className="flex justify-between font-['Nunito']">
                      <span className="text-[#6B665E]">Subtotal</span>
                      <span className="text-[#2D2A26]">${cart.total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-['Nunito'] text-sm mt-1">
                      <span className="text-[#6B665E]">Shipping</span>
                      <span className="text-[#6B665E]">Calculated at checkout</span>
                    </div>
                  </div>

                  <div className="border-t border-[#E5E0D5] pt-4">
                    <div className="flex justify-between font-['Nunito'] font-bold text-lg">
                      <span className="text-[#2D2A26]">Total</span>
                      <span className="text-[var(--liturgical-primary)]" data-testid="cart-total">${cart.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  <Button
                    onClick={handleCheckout}
                    disabled={checkingOut || updating}
                    className="w-full bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] font-semibold py-6"
                    data-testid="checkout-btn"
                  >
                    {checkingOut ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Checkout - ${cart.total?.toFixed(2)}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-center text-[#6B665E] font-['Nunito']">
                    Secure checkout powered by Stripe
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
